/**
 * What a partner sees of their own account (REQ-018 AC-004, AC-008).
 *
 * This page used to be four hard-coded numbers — "€24,500 monthly revenue",
 * "189 conversions" — shown to real partners as if they were theirs. That is
 * worse than an empty page: an empty page is honest about knowing nothing,
 * and an invented commission figure is a claim about money.
 *
 * Everything here now comes from the partner's own record, scoped server-side
 * by the id on their user document. Where a figure cannot be traced to settled
 * payments it is not shown at all (AC-006), and where the account is not yet
 * linked the page says exactly that rather than drawing zeroes.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Building2, Link2, Loader2, Users } from "lucide-react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import { useAuthStore } from "@/stores/authStore";

interface PartnerSelf {
  linked: boolean;
  reason?: string;
  partner: {
    organisation: string;
    typeLabel: string;
    earns: string;
    stage: string;
    tier: string;
    referralCodes: string[];
    attributedCustomers: number;
    payingCustomers: number;
    lastAttributionAt: string | null;
    contract: {
      startsOn: string | null;
      endsOn: string | null;
      commissionBps: number | null;
    };
  } | null;
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-3xl font-semibold tabular-nums text-gray-900">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

export const AffiliatePartnerAdminPage: React.FC = () => {
  const { user } = useAuthStore();
  const fmt = useLocaleFormat();

  const self = useQuery({
    queryKey: ["partner", "me"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: PartnerSelf }>(
        "/partner/me",
      );
      return response.data.data;
    },
  });

  const partner = self.data?.partner;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Building2 className="h-6 w-6 text-sky-600" />
            {partner?.organisation ?? "Partner"}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Welcome back, {user?.firstName} {user?.lastName}
            {partner && ` — ${partner.typeLabel}, ${partner.tier} tier`}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        {self.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your account…
          </div>
        ) : !self.data?.linked || !partner ? (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">No partner record is linked yet.</p>
              <p className="mt-1">
                {self.data?.reason ??
                  "HealthPass staff connect your account to your partner record."}{" "}
                Until then there are no figures to show — this is not a reading
                of zero.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat
                label="People you introduced"
                value={String(partner.attributedCustomers)}
                hint="Registrations carrying one of your referral codes"
              />
              <Stat
                label="Of those, subscribed"
                value={String(partner.payingCustomers)}
                hint="On a PLUS or PREMIUM plan today"
              />
              <Stat label="Status" value={partner.stage} hint={partner.earns} />
            </div>

            {/* Commission is deliberately absent until it can be traced to
                settled payments (AC-006). A rate is a term of the contract; an
                amount would be a claim about money that has arrived. */}
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
              <p className="font-medium text-gray-900">Commission</p>
              {partner.contract.commissionBps ? (
                <p className="mt-1 text-gray-600">
                  Your agreed rate is{" "}
                  {(partner.contract.commissionBps / 100).toFixed(2)}%.
                  Statements are produced from settled payments; no figure is
                  shown here until it can be traced to money that has actually
                  arrived.
                </p>
              ) : (
                <p className="mt-1 text-gray-600">
                  No commission rate is recorded on your contract.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Link2 className="h-3.5 w-3.5" /> Your referral codes
              </p>
              {partner.referralCodes.length === 0 ? (
                <p className="mt-2 text-sm text-gray-600">None issued yet.</p>
              ) : (
                <ul className="mt-2 flex flex-wrap gap-2">
                  {partner.referralCodes.map((code) => (
                    <li
                      key={code}
                      className="rounded bg-gray-100 px-3 py-1 font-mono text-sm text-gray-800"
                    >
                      {code}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Users className="h-3.5 w-3.5" /> Who they are
              </p>
              <p className="mt-2">
                Counts only. The people you introduce are HealthPass customers
                and their identities are not shared back — including with the
                partner who introduced them.
              </p>
              {partner.lastAttributionAt && (
                <p className="mt-2">
                  Most recent introduction:{" "}
                  {fmt.date(partner.lastAttributionAt)}.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AffiliatePartnerAdminPage;
